import { useMemo, useState } from 'react'
import { Download } from 'lucide-react'
import { useParams } from 'react-router'

import Flex from '@/elements/Flex'
import Text from '@/elements/Text'
import Loading from '@/elements/Loading'
import Button from '@/elements/Button'
import FormSelect from '@/elements/FormSelect'
import Tabs from '@/components/Tabs'
import WatchdogAlertList from '@/components/WatchdogAlertList'
import ExportModal from '@/components/ExportModal'
import usePagination from '@/hooks/usePagination'
import useFiltering from '@/hooks/useFiltering'
import useSortOptions, { type SortWithDirection } from '@/hooks/useSortOptions'
import { useGetAssignedAlertCounts, useGetAssignedAlerts } from '@/api/alert'
import { AlertStatuses } from '@/api/types/Alert'
import { useGetMyAlertsIndividuals } from '@/api/user'
import type { UserAssignmentResponse } from '@/api/types/User'
import watchdogTabs, { type WatchdogTabsKeys } from '@/router/tabsSystems/watchdogTabs'
import { formatAsTitle } from '@/utils/format'
import useBreakpoints from '@/hooks/useBreakpoints'

function MyAlerts() {
  // Stores & Params
  const { status } = useParams<{ status: WatchdogTabsKeys }>()
  const { isMobile } = useBreakpoints()

  // Pagination & Filtering
  const { page, limit, setPage } = usePagination(10)
  const { updateSortBy, sortBy, sortDir, updateFilterBy, allFilters } = useFiltering<['subjectId']>()

  // Alerts fetching
  const { data: alertsData, isLoading: alertsLoading } = useGetAssignedAlerts({
    page,
    limit,
    filters: {
      ...(status === 'all' ? undefined : status && { 'filter[status]': status.toUpperCase() }),
      ...(allFilters.subjectId ? { 'filter[subjectId]': allFilters.subjectId } : {}),
      ...(sortBy ? { [`sort[${sortBy}]`]: sortDir || 'ASC' } : {}),
    },
  })
  const { data: alertCounts } = useGetAssignedAlertCounts(
    { ...(allFilters.subjectId ? { 'filter[subjectId]': allFilters.subjectId } : {}) },
  )

  // Individuals fetching
  const { data: individuals, isLoading: isLoadingIndividuals } = useGetMyAlertsIndividuals()

  // Derived Data
  const alerts = alertsData?.data?.items || []

  const tabsExtraDataFromAlerts = useMemo(() => {
    const alertCountsTotal = alertCounts?.data?.total || 0
    const alertCountsData = alertCounts?.data?.counts || {}

    return {
      'all': `(${alertCountsTotal})`,
      ...Object.fromEntries(
        AlertStatuses.map(status => [
          status.toLowerCase(),
          `(${alertCountsData[status] || 0})`
        ])
      )
    } as Record<WatchdogTabsKeys, string>
  }, [alertCounts])

  const availableIndividuals = useMemo(() => {
    return (individuals?.data || []).sort((a, b) => {
      const nameA = a.details?.fullName || ''
      const nameB = b.details?.fullName || ''
      return nameA.localeCompare(nameB)
    })
  }, [individuals?.data])
  const sortByOptions = useMemo(
    () => alertsData?.data?.filters?.sortableBy || [],
    [alertsData?.data?.filters?.sortableBy]
  )
  const { sortOptions, selectedSortOption } = useSortOptions({
    sortByOptions,
    sortBy,
    sortDir,
  })

  const [showExportModal, setShowExportModal] = useState(false)

  return (
    <Flex component='section' gap={4} grow>
      <Flex inline={!isMobile} justify='space-between' wfull gap={3} wrap align='start'>
        <Text variant='h1'>My Alerts</Text>
        <Flex wfull inline gap={2} wrap justify='flex-end' align='end' grow>
          <FormSelect<SortWithDirection>
          allowUnselect
          optionsAutoWidth
          values={selectedSortOption ? [selectedSortOption] : []}
          onChange={([v]) => updateSortBy(v?.value, v?.dir)}
          optionLabel={item => formatAsTitle(item.value) + ` ${item.dirLabel}`}
          optionValue={item => `${item.value}-${item.dir}`}
          name='sort-by'
          label='Sort by'
          placeholder='Default'
          options={sortOptions}
          disabled={alertsLoading}
          style={{ width: isMobile ? '100%' : 'auto' }}
        />
        <FormSelect<UserAssignmentResponse>
          allowUnselect
          name='individualsSelect'
          label='Filter by'
          placeholder='Select Individual'
          options={availableIndividuals as UserAssignmentResponse[]}
          values={
            allFilters.subjectId
              ? availableIndividuals.filter(ind => ind.subject?.id === allFilters.subjectId)
              : []
          }
          optionLabel={item => item?.details?.fullName || ''}
          optionValue={item => item?.subject?.id || ''}
          onChange={([v]) => {
            if (v?.subject?.id) {
              updateFilterBy('subjectId', v.subject.id)
            } else {
              updateFilterBy('subjectId', '')
            }
          }}
          style={{
            width: isMobile ? '100%' : 'auto',
            minWidth: !isMobile ? 280 : 'auto'
          }}
          disabled={isLoadingIndividuals}
          optionsAutoWidth
          filterBy={(option, search) =>
            option.details.fullName.toLowerCase().includes(search.toLowerCase())
          }
        />
          <Button
            variant='iconBtn'
            onClick={() => setShowExportModal(true)}
            styles={{ width: 54, height: 54, minWidth: 54, minHeight: 54 }}
          >
            <Download size={20} />
          </Button>
        </Flex>
      </Flex>
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        mode='myAlerts'
        subjectId={allFilters.subjectId || undefined}
      />
      <Tabs
        tabs={watchdogTabs}
        extraInfo={tabsExtraDataFromAlerts}
        keepQueryParamsButExclude={['page']}
        grow
      >
        {alertsLoading ? (
          <Flex wfull py={8} grow>
            <Loading />
          </Flex>
        ) : (
          <WatchdogAlertList
            showIndividual
            alerts={alerts}
            pagination={alertsData?.data?.pagination}
            onPageChange={setPage}
            clearFilters={() => updateFilterBy('subjectId', '')}
          />
        )}
      </Tabs>
    </Flex>
  )
}

export default MyAlerts
